import { useEffect, useMemo, useRef, useState } from "react";
import { Container, Card, Form, Button, Spinner, Alert, Tabs, Tab } from "react-bootstrap";
import {
  getTextRecommendationSettings, updateTextRecommendationSettings,
  getMyTextRecommendationSettings, updateMyTextRecommendationSettings,
  getAllUsers, searchTextRecommendationKiinteistot, getLlmReportTypes,
} from "../api/api";

// One personal-override row (switch-type field): "Oma asetus" toggle decides
// whether this field is overridden at all — off shows (and uses) the admin
// default, grayed out; on reveals your own editable switch, seeded from the
// admin default the moment it's turned on.
function OverrideSwitchRow({ label, help, adminValue, value, onChange }) {
  const overridden = value !== null;
  return (
    <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
      <div>
        <div className="d-flex align-items-center gap-2">
          <span>{label}</span>
          <Form.Check
            type="switch"
            id={`${label}-override`}
            label={<span style={{ fontSize: "0.72rem", color: "#6c757d" }}>Oma asetus</span>}
            checked={overridden}
            onChange={e => onChange(e.target.checked ? adminValue : null)}
          />
        </div>
        {help && <div className="text-muted" style={{ fontSize: "0.78rem" }}>{help}</div>}
      </div>
      <Form.Check
        type="switch"
        checked={overridden ? value : adminValue}
        disabled={!overridden}
        onChange={e => onChange(e.target.checked)}
      />
    </Form.Group>
  );
}

// Same idea, number-type field.
function OverrideNumberRow({ label, help, adminValue, value, onChange, min, max }) {
  const overridden = value !== null;
  return (
    <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
      <div>
        <div className="d-flex align-items-center gap-2">
          <span>{label}</span>
          <Form.Check
            type="switch"
            id={`${label}-override`}
            label={<span style={{ fontSize: "0.72rem", color: "#6c757d" }}>Oma asetus</span>}
            checked={overridden}
            onChange={e => onChange(e.target.checked ? adminValue : null)}
          />
        </div>
        {help && <div className="text-muted" style={{ fontSize: "0.78rem" }}>{help}</div>}
      </div>
      <Form.Control
        type="number" min={min} max={max} style={{ width: 90 }}
        value={overridden ? value : adminValue}
        disabled={!overridden}
        onChange={e => onChange(Number(e.target.value))}
      />
    </Form.Group>
  );
}

// Searchable multi-select over kayttajatiedot (client-side filter — getAllUsers
// has no search param and the user list is small, see api.js:613). Selected
// users render as removable chips; same "chip + search dropdown" shape as
// CoverPageForm.jsx's PrimaryTemplatePicker, but multi-select.
function UserMultiSelect({ selectedIds, onChange }) {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    getAllUsers().then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const byId = useMemo(() => new Map(users.map(u => [Number(u.id), u])), [users]);
  const selected = selectedIds.map(id => byId.get(id) || { id, nimi: `Käyttäjä ${id}` });

  // Empty query -> list everyone (not yet selected), so you can browse
  // without knowing an exact name first; typing narrows by name OR email
  // (several users can share a name, email disambiguates).
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    const available = users.filter(u => !selectedIds.includes(Number(u.id)));
    const matches = term
      ? available.filter(u => (u.nimi || "").toLowerCase().includes(term) || (u.sahkoposti || "").toLowerCase().includes(term))
      : available;
    return matches
      .slice()
      .sort((a, b) => (a.nimi || "").localeCompare(b.nimi || ""))
      .slice(0, 20);
  }, [query, users, selectedIds]);

  const add = (id) => {
    onChange([...selectedIds, Number(id)]);
    setQuery("");
  };
  const remove = (id) => onChange(selectedIds.filter(x => x !== id));

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map(u => (
            <span key={u.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f3f4f6", border: "1px solid #e5e5ea", borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: "0.82rem", color: "#1c1c1e" }}>
              {u.nimi || `Käyttäjä ${u.id}`}{u.sahkoposti && <span style={{ color: "#8e8e93" }}>&nbsp;· {u.sahkoposti}</span>}
              <button type="button" onClick={() => remove(Number(u.id))} title="Poista"
                style={{ border: "none", background: "none", color: "#8e8e93", fontSize: "0.85rem", cursor: "pointer", padding: 0, lineHeight: 1 }}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: "relative", maxWidth: 360 }}>
        <Form.Control
          type="text"
          placeholder="Hae tai selaa käyttäjiä…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && (
          <div style={{ position: "absolute", zIndex: 10, top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e5ea", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", maxHeight: 260, overflowY: "auto" }}>
            {results.length === 0 && <div style={{ padding: 10, fontSize: "0.82rem", color: "#8e8e93" }}>Ei osumia.</div>}
            {results.map(u => (
              <div key={u.id} onMouseDown={() => add(u.id)}
                style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid #f1f3f5" }}>
                <div style={{ fontSize: "0.86rem", color: "#1c1c1e", fontWeight: 600 }}>{u.nimi || `Käyttäjä ${u.id}`}</div>
                {u.sahkoposti && <div style={{ fontSize: "0.74rem", color: "#8e8e93" }}>{u.sahkoposti}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Searchable multi-select over kiinteistöt — server-side search (unlike
// UserMultiSelect, the kiinteistö list can be large, so there's no client-side
// cache; /kiinteisto-search handles both blank-query browse and typed filter).
// Selected ones already carry kohteen_nimi/osoite from the search result, so
// they can be rendered as chips without a separate lookup-by-id round trip.
function KiinteistoMultiSelect({ selected, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const selectedIds = selected.map(k => k.id);

  useEffect(() => {
    if (!open) return;
    setSearching(true);
    const t = setTimeout(() => {
      searchTextRecommendationKiinteistot({ q: query.trim() })
        .then(r => setResults((r.data || []).filter(k => !selectedIds.includes(k.id))))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  const add = (k) => {
    onChange([...selected, k]);
    setQuery("");
  };
  const remove = (id) => onChange(selected.filter(k => k.id !== id));

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map(k => (
            <span key={k.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f3f4f6", border: "1px solid #e5e5ea", borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: "0.82rem", color: "#1c1c1e" }}>
              {k.kohteen_nimi || k.osoite || `Kohde ${k.id}`}
              <button type="button" onClick={() => remove(k.id)} title="Poista"
                style={{ border: "none", background: "none", color: "#8e8e93", fontSize: "0.85rem", cursor: "pointer", padding: 0, lineHeight: 1 }}>
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: "relative", maxWidth: 360 }}>
        <Form.Control
          type="text"
          placeholder="Hae tai selaa kohteita…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && (
          <div style={{ position: "absolute", zIndex: 10, top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e5e5ea", borderRadius: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", maxHeight: 260, overflowY: "auto" }}>
            {searching && <div style={{ padding: 10, fontSize: "0.82rem", color: "#8e8e93" }}>Haetaan…</div>}
            {!searching && results.length === 0 && <div style={{ padding: 10, fontSize: "0.82rem", color: "#8e8e93" }}>Ei osumia.</div>}
            {!searching && results.map(k => (
              <div key={k.id} onMouseDown={() => add(k)}
                style={{ padding: "8px 10px", cursor: "pointer", borderBottom: "1px solid #f1f3f5" }}>
                <div style={{ fontSize: "0.86rem", color: "#1c1c1e", fontWeight: 600 }}>{k.kohteen_nimi || `Kohde ${k.id}`}</div>
                {k.osoite && <div style={{ fontSize: "0.74rem", color: "#8e8e93" }}>{k.osoite}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Groups of reports.type values that should recommend to/from each other —
// a freshly-versioned report template (e.g. a "_v2" rewrite) usually has far
// fewer reports of its own than its predecessor, especially right after it
// ships, so without this it gets no suggestions until enough new-type
// reports pile up on their own. A type not listed in any group just matches
// itself, same as before this existed.
function TypeGroupEditor({ groups, allTypes, onChange }) {
  const typeName = (code) => allTypes.find(t => t.type === code)?.name || code;
  const updateGroup = (idx, nextTypes) => onChange(groups.map((g, i) => i === idx ? nextTypes : g));
  const removeGroup = (idx) => onChange(groups.filter((_, i) => i !== idx));
  const addGroup = () => onChange([...groups, []]);

  return (
    <div>
      {groups.map((group, idx) => {
        const available = allTypes.filter(t => !group.includes(t.type));
        return (
          <div key={idx} className="border rounded p-2 mb-2" style={{ borderColor: "#e5e5ea" }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: group.length ? 8 : 0 }}>
              {group.map(code => (
                <span key={code} style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#f3f4f6", border: "1px solid #e5e5ea", borderRadius: 999, padding: "3px 6px 3px 10px", fontSize: "0.82rem", color: "#1c1c1e" }}>
                  {typeName(code)}
                  <button type="button" onClick={() => updateGroup(idx, group.filter(c => c !== code))} title="Poista ryhmästä"
                    style={{ border: "none", background: "none", color: "#8e8e93", fontSize: "0.85rem", cursor: "pointer", padding: 0, lineHeight: 1 }}>
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Form.Select size="sm" style={{ width: "auto" }} value=""
                onChange={e => { if (e.target.value) updateGroup(idx, [...group, e.target.value]); }}>
                <option value="">+ Lisää raporttityyppi…</option>
                {available.map(t => <option key={t.type} value={t.type}>{t.name}</option>)}
              </Form.Select>
              <Button size="sm" variant="outline-danger" onClick={() => removeGroup(idx)}>Poista ryhmä</Button>
            </div>
          </div>
        );
      })}
      <Button size="sm" variant="outline-success" onClick={addGroup}>+ Uusi ryhmä</Button>
    </div>
  );
}

// Admin settings for the "text recommendations" feature (LexicalShowcase's
// recommendation banner) — deliberately separate from /llm-promptit. The
// underlying report/block addressing is the same convention that feature
// uses, but this is its own feature with its own settings, not a tab there.
//
// A recommendation is compared within the current report's own type by
// default, OR any type in the same type_groups entry if it's in one (below)
// — "report type scope" from the original mockup, otherwise dropped, since
// there was no second behavior defined for it before type_groups existed.
export default function Tekstisuositukset() {
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("omat");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    year_range: 10,
    ready_only: true,
    max_suggestions: 4,
    exclude_user_ids: [],
    exclude_kiinteisto_ids: [],
    match_rakennusluokitus: false,
    use_sijainti: true,
    only_own_reports: false,
    wk3_section_templates_enabled: true,
    type_groups: [],
  });
  // Display objects (id + kohteen_nimi/osoite) for the chips — settings only
  // stores ids; resolved once on load via kiinteisto-search's `ids` param.
  const [excludedKiinteistot, setExcludedKiinteistot] = useState([]);
  const [allReportTypes, setAllReportTypes] = useState([]); // for TypeGroupEditor

  // Personal overrides, for the currently logged-in user only — null on any
  // of these six means "use the admin default" (not yet overridden);
  // exclude_user_ids is additive to the admin's list, not a replacement, so
  // it has no null state, just its own (possibly empty) array.
  const [mySettings, setMySettings] = useState({
    ready_only: null, year_range: null, match_rakennusluokitus: null, max_suggestions: null,
    use_sijainti: null, only_own_reports: null,
    exclude_user_ids: [],
  });

  // Snapshot of what's actually saved, so the UI can tell the difference
  // between "loaded" and "edited but not saved yet" — the save bar below is
  // only prominent/warns-on-leave when the two have drifted apart.
  const savedRef = useRef(null);

  useEffect(() => {
    getLlmReportTypes().then(r => setAllReportTypes(r.data ?? [])).catch(() => setAllReportTypes([]));
    Promise.all([getTextRecommendationSettings(), getMyTextRecommendationSettings()])
      .then(([adminRes, myRes]) => {
        // "enabled" (the global on/off switch) has no control on this page
        // right now — the feature is already admin-only via the rollout
        // gate, so a separate "enabled for everyone" toggle is redundant/
        // confusing while that's true. Forced true here so saving the page
        // never silently flips it off; restore the toggle (removed above)
        // if/when the admin-only gate comes off and "for everyone" becomes
        // a real, distinct choice again.
        const nextSettings = { ...settings, ...adminRes.data, enabled: true };
        const nextMySettings = {
          ...mySettings, ...myRes.data,
          ready_only: myRes.data.ready_only ?? null,
          year_range: myRes.data.year_range ?? null,
          match_rakennusluokitus: myRes.data.match_rakennusluokitus ?? null,
          max_suggestions: myRes.data.max_suggestions ?? null,
          use_sijainti: myRes.data.use_sijainti ?? null,
          only_own_reports: myRes.data.only_own_reports ?? null,
          exclude_user_ids: myRes.data.exclude_user_ids || [],
        };
        setSettings(nextSettings);
        setMySettings(nextMySettings);
        savedRef.current = JSON.stringify([nextSettings, nextMySettings]);
        const ids = adminRes.data.exclude_kiinteisto_ids || [];
        if (ids.length) {
          searchTextRecommendationKiinteistot({ ids }).then(res => setExcludedKiinteistot(res.data || [])).catch(() => {});
        }
      })
      .catch(e => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = !loading && savedRef.current !== null && JSON.stringify([settings, mySettings]) !== savedRef.current;

  // Closing the tab or navigating away with unsaved edits is the easiest way
  // to silently lose them — warn, same as every other unsaved-form guard.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await Promise.all([
        updateTextRecommendationSettings(settings),
        updateMyTextRecommendationSettings(mySettings),
      ]);
      savedRef.current = JSON.stringify([settings, mySettings]);
      setSaved(true);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-5" style={{ maxWidth: 700, paddingBottom: 76 }}>
      <div className="text-muted small mb-1">Hallinta</div>
      <div className="d-flex align-items-baseline justify-content-between mb-4">
        <h1 className="h4 mb-0">Tekstisuositusten asetukset</h1>
        <span className="small fw-semibold" style={{ color: "#92400e", background: "#fff7ed", border: "1px solid #fde68a", borderRadius: 999, padding: "3px 10px" }}>
          Ehdotukset näkyvät vain ylläpitäjille toistaiseksi
        </span>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Asetukset tallennettu.</Alert>}

      <Tabs activeKey={tab} onSelect={k => setTab(k || "omat")} className="mb-3">
        <Tab eventKey="omat" title="Omat asetukset">
          <Card className="mb-3" style={{ borderTopLeftRadius: 0 }}>
            <Card.Body>
              <div className="text-muted small mb-3">
                Ylittävät "Yleiset oletukset" -välilehden asetukset vain sinulle — kukaan muu ei näe näitä.
              </div>

              <OverrideSwitchRow
                label="Vain valmiit raportit"
                adminValue={settings.ready_only}
                value={mySettings.ready_only}
                onChange={v => setMySettings(s => ({ ...s, ready_only: v }))}
              />
              <OverrideSwitchRow
                label="Sama rakennusluokitus"
                adminValue={settings.match_rakennusluokitus}
                value={mySettings.match_rakennusluokitus}
                onChange={v => setMySettings(s => ({ ...s, match_rakennusluokitus: v }))}
              />
              <OverrideNumberRow
                label="Rakennusvuosi ± vuotta"
                adminValue={settings.year_range}
                value={mySettings.year_range}
                onChange={v => setMySettings(s => ({ ...s, year_range: v }))}
                min={0} max={50}
              />
              <OverrideNumberRow
                label="Ehdotusten enimmäismäärä"
                adminValue={settings.max_suggestions}
                value={mySettings.max_suggestions}
                onChange={v => setMySettings(s => ({ ...s, max_suggestions: v }))}
                min={1} max={10}
              />
              <OverrideSwitchRow
                label="Sijainti"
                help="Maantieteellinen etäisyys ratkaisee tasapelit, kun muut kriteerit (rakennustyyppi, rakennusvuosi, oma raportti) ovat yhtä hyviä."
                adminValue={settings.use_sijainti}
                value={mySettings.use_sijainti}
                onChange={v => setMySettings(s => ({ ...s, use_sijainti: v }))}
              />
              <OverrideSwitchRow
                label="Näytä vain omat raportit"
                help="Ehdota ainoastaan raporteista, joissa olet itse ollut tiiminä."
                adminValue={settings.only_own_reports}
                value={mySettings.only_own_reports}
                onChange={v => setMySettings(s => ({ ...s, only_own_reports: v }))}
              />

              <Form.Group className="py-2 border-top">
                <div className="mb-2">
                  <div>Ohita näiden käyttäjien raportit <span className="text-muted" style={{ fontWeight: 400 }}>(vain omista ehdotuksistasi)</span></div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Lisäys "Yleiset oletukset" -välilehden vastaavan listan päälle — vaikuttaa vain sinun näkemiisi ehdotuksiin.
                  </div>
                </div>
                <UserMultiSelect
                  selectedIds={mySettings.exclude_user_ids}
                  onChange={ids => setMySettings(s => ({ ...s, exclude_user_ids: ids }))}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="yleiset" title="Yleiset oletukset">
          <Card className="mb-3" style={{ borderTopLeftRadius: 0 }}>
            <Card.Body>
              <div className="fw-bold mb-1">Hakuehdot</div>
              <div className="text-muted small mb-3">Mitkä raportit lasketaan "samankaltaisiksi".</div>

              <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
                <div>
                  <div>Rakennusvuosi ± vuotta</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Verrataan kiinteistön päärakennuksen rakennusvuoteen.
                  </div>
                </div>
                <Form.Control
                  type="number" min={0} max={50} style={{ width: 90 }}
                  value={settings.year_range}
                  onChange={e => setSettings(s => ({ ...s, year_range: Number(e.target.value) }))}
                />
              </Form.Group>

              <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
                <div>
                  <div>Sama rakennusluokitus</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Vaadi myös sama rakennusluokitus (esim. kerrostalo, rivitalo) kuin nykyisen raportin kiinteistön päärakennuksella.
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id="matchRakennusluokitus"
                  checked={settings.match_rakennusluokitus}
                  onChange={e => setSettings(s => ({ ...s, match_rakennusluokitus: e.target.checked }))}
                />
              </Form.Group>

              <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
                <div>
                  <div>Sijainti</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Maantieteellinen etäisyys ratkaisee tasapelit, kun muut kriteerit (rakennustyyppi, rakennusvuosi, oma raportti) ovat yhtä hyviä.
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id="useSijainti"
                  checked={settings.use_sijainti}
                  onChange={e => setSettings(s => ({ ...s, use_sijainti: e.target.checked }))}
                />
              </Form.Group>

              <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
                <div>
                  <div>Näytä vain omat raportit</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Ehdota ainoastaan raporteista, joissa käyttäjä on itse ollut tiiminä — muuten tämä on vain yksi ehdotusten järjestykseen vaikuttava merkki.
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id="onlyOwnReports"
                  checked={settings.only_own_reports}
                  onChange={e => setSettings(s => ({ ...s, only_own_reports: e.target.checked }))}
                />
              </Form.Group>

              <Form.Group className="d-flex align-items-center justify-content-between py-2 border-top">
                <div>
                  <div>Vain valmiit raportit</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Jätä luonnokset ja arkistoidut raportit pois ehdotuksista.
                  </div>
                </div>
                <Form.Check
                  type="switch"
                  id="readyOnly"
                  checked={settings.ready_only}
                  onChange={e => setSettings(s => ({ ...s, ready_only: e.target.checked }))}
                />
              </Form.Group>

              <Form.Group className="py-2 border-top">
                <div className="mb-2">
                  <div>Ohita näiden käyttäjien raportit</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Raportit joissa joku näistä on tiiminä (report_users) jätetään kokonaan pois ehdotuksista — esim. dev/testiraportit.
                  </div>
                </div>
                <UserMultiSelect
                  selectedIds={settings.exclude_user_ids}
                  onChange={ids => setSettings(s => ({ ...s, exclude_user_ids: ids }))}
                />
              </Form.Group>

              <Form.Group className="py-2 border-top">
                <div className="mb-2">
                  <div>Ohita nämä kohteet</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Nämä kohteet jätetään aina kokonaan pois ehdotuksista, riippumatta rakennusvuodesta tai tiimistä.
                  </div>
                </div>
                <KiinteistoMultiSelect
                  selected={excludedKiinteistot}
                  onChange={items => {
                    setExcludedKiinteistot(items);
                    setSettings(s => ({ ...s, exclude_kiinteisto_ids: items.map(k => k.id) }));
                  }}
                />
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Body>
              <div className="fw-bold mb-1">Raporttityyppien ryhmät</div>
              <div className="text-muted small mb-3">
                Samaan ryhmään kuuluvat raporttityypit ehdottavat tekstejä toisilleen — esim. uudella raporttipohjan
                versiolla ei vielä ole omia valmiita raportteja, mutta se voi silti hyödyntää vanhan version raportteja.
                Raporttityyppi joka ei ole missään ryhmässä toimii kuten ennenkin, vain saman tyypin kesken.
              </div>
              <TypeGroupEditor
                groups={settings.type_groups}
                allTypes={allReportTypes}
                onChange={groups => setSettings(s => ({ ...s, type_groups: groups }))}
              />
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Body>
              <div className="fw-bold mb-1">WK3-raportit</div>
              <div className="text-muted small mb-3">
                Rakennetekninen, LVI- ja sähköjärjestelmien kuntoarviot voivat käyttää omaa malliraporttia, erillään muusta raportista.
              </div>
              <Form.Check
                type="switch"
                id="wk3SectionTemplatesEnabled"
                label="Osiokohtaiset mallipohjat käytössä"
                checked={settings.wk3_section_templates_enabled}
                onChange={e => setSettings(s => ({ ...s, wk3_section_templates_enabled: e.target.checked }))}
              />
              <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                Kun pois päältä, WK3-raportitkin käyttävät vain yhtä koko raportin malliraporttia, kuten muutkin raporttityypit.
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Body>
              <div className="fw-bold mb-3">Näyttö</div>
              <Form.Group className="d-flex align-items-center justify-content-between">
                <div>
                  <div>Näytettävien ehdotusten enimmäismäärä</div>
                  <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                    Jos osumia on enemmän, näytetään lähimmät rakennusvuoden perusteella.
                  </div>
                </div>
                <Form.Control
                  type="number" min={1} max={10} style={{ width: 90 }}
                  value={settings.max_suggestions}
                  onChange={e => setSettings(s => ({ ...s, max_suggestions: Number(e.target.value) }))}
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, background: "#fff", borderTop: "1px solid #e9ecef", boxShadow: "0 -2px 8px rgba(0,0,0,0.04)", zIndex: 10 }}>
        <div className="mx-auto d-flex align-items-center justify-content-between px-3" style={{ maxWidth: 700, padding: "12px 12px" }}>
          <span className="small fw-semibold" style={{ color: dirty ? "#92400e" : "transparent", visibility: dirty ? "visible" : "hidden" }}>
            Tallentamattomia muutoksia
          </span>
          <Button variant={dirty ? "primary" : "success"} onClick={handleSave} disabled={saving || !dirty}>
            {saving ? "Tallennetaan…" : "Tallenna"}
          </Button>
        </div>
      </div>
    </Container>
  );
}
